import apiClient from './client';

export interface Label {
  id: number;
  account_id: number;
  account_name: string;
  name: string;
  display_name: string | null;
  category: string | null;
  is_active: boolean;
  data_count: number;
}

export interface LabelListResponse {
  items: Label[];
  total: number;
}

export interface LabelListParams {
  company_id: number;
  account_id?: number;
  is_active?: boolean;
  search?: string;
}

export interface LabelUpdateInput {
  display_name?: string | null;
  category?: string | null;
  is_active?: boolean;
}

export interface DeleteResponse {
  message: string;
  deleted_count: number;
}

export interface DeleteDataParams {
  start_date?: string;
  end_date?: string;
}

export interface GoogleSheetsExportInput {
  spreadsheet_url: string;
  sheet_name: string;
  label_ids: number[];
}

export interface GoogleSheetsExportResponse {
  message: string;
  exported_count: number;
  spreadsheet_url: string;
}

export interface GoogleSheetsImportInput {
  spreadsheet_url: string;
  sheet_name: string;  // シート名 = 取得先名 = タグ名
  company_id: number;
  register_to_utage?: boolean;
}

export interface GoogleSheetsImportResponse {
  message: string;
  imported_count: number;
  skipped_count: number;
  label_names: string[];
}

export const labelsApi = {
  // ラベル一覧取得
  getLabels: async (params: LabelListParams): Promise<LabelListResponse> => {
    const response = await apiClient.get<LabelListResponse>('/api/labels', { params });
    return response.data;
  },

  // ラベル更新
  updateLabel: async (id: number, data: LabelUpdateInput): Promise<Label> => {
    const response = await apiClient.patch<Label>(`/api/labels/${id}`, data);
    return response.data;
  },

  // ラベル削除（関連データも削除）
  deleteLabel: async (id: number): Promise<DeleteResponse> => {
    const response = await apiClient.delete<DeleteResponse>(`/api/labels/${id}`);
    return response.data;
  },

  // ラベルのデータを日付範囲で削除
  deleteLabelData: async (id: number, params?: DeleteDataParams): Promise<DeleteResponse> => {
    const response = await apiClient.delete<DeleteResponse>(`/api/labels/${id}/data`, { params });
    return response.data;
  },

  // 単一データポイントを削除
  deleteSingleData: async (dataId: number): Promise<DeleteResponse> => {
    const response = await apiClient.delete<DeleteResponse>(`/api/labels/data/${dataId}`);
    return response.data;
  },

  // パターンに一致するラベルを一括無効化
  bulkDisableLabels: async (companyId: number, pattern: string): Promise<DeleteResponse> => {
    const response = await apiClient.post<DeleteResponse>('/api/labels/bulk-disable', null, {
      params: { company_id: companyId, pattern },
    });
    return response.data;
  },

  // パターンに一致するラベルを一括削除
  bulkDeleteLabels: async (companyId: number, pattern: string): Promise<DeleteResponse> => {
    const response = await apiClient.delete<DeleteResponse>('/api/labels/bulk-delete', {
      params: { company_id: companyId, pattern },
    });
    return response.data;
  },

  // 指定IDのラベルを一括有効化/無効化
  bulkUpdateByIds: async (labelIds: number[], isActive: boolean): Promise<DeleteResponse> => {
    const response = await apiClient.post<DeleteResponse>('/api/labels/bulk-update-by-ids', {
      label_ids: labelIds,
      is_active: isActive,
    });
    return response.data;
  },

  // 指定IDのラベルを一括削除
  bulkDeleteByIds: async (labelIds: number[]): Promise<DeleteResponse> => {
    const response = await apiClient.post<DeleteResponse>('/api/labels/bulk-delete-by-ids', {
      label_ids: labelIds,
    });
    return response.data;
  },

  // Googleスプレッドシートにエクスポート
  exportToGoogleSheets: async (data: GoogleSheetsExportInput): Promise<GoogleSheetsExportResponse> => {
    const response = await apiClient.post<GoogleSheetsExportResponse>('/api/labels/export-to-sheets', data);
    return response.data;
  },

  // Googleスプレッドシートからインポート
  importFromGoogleSheets: async (data: GoogleSheetsImportInput): Promise<GoogleSheetsImportResponse> => {
    const response = await apiClient.post<GoogleSheetsImportResponse>('/api/labels/import-from-sheets', data);
    return response.data;
  },
};
